/**
 * AI 規定コマンド利用状況マトリクス panel.
 *
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business 人 or PJ を行、マスタの工程を列にした規定コマンド実行数マトリクスと、母集団のサマリ (利用あり / 規定コマンド 0 / データなし) を描画する presentation component。数値セルは hover でコマンド別内訳をオーバーレイ表示し、行の「内訳」でコマンド粒度の内訳モーダルを開く。PJ 軸では Backlog リンク・ステータス・担当者を表示して完了 PJ を既定で隠す。fetch は親が行い data を渡す
 * @graph-connects CcCommandMatrixDetailModal [calls] 行内訳モーダルの表示
 * @graph-connects PhaseBreakdownCells [calls] 工程セルの hover 内訳オーバーレイ表示
 * @graph-connects CommandTotalBreakdownCell [calls] 規定コマンド計セルの hover 内訳オーバーレイ表示
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Badge } from '@cortex/web-design-system/components/ui/Badge';
import { Card, CardContent, CardHeader } from '@cortex/web-design-system/components/ui/Card';
import type {
  CcCommandMatrixAxis,
  CcCommandMatrixMasterMeta,
  CcCommandMatrixMemberRow,
  CcCommandMatrixProjectRow,
} from '../../../types/api.js';
import {
  backlogTicketUrl,
  CcCommandMatrixDetailModal,
  type CcCommandMatrixDetailSelection,
} from './CcCommandMatrixDetailModal.js';
import { CommandTotalBreakdownCell, PhaseBreakdownCells } from './CcCommandMatrixHoverCells.js';

export interface CcCommandMatrixPanelProps {
  /** 行の軸 (人 / PJ) */
  axis: CcCommandMatrixAxis;
  /** マスタの工程一覧。列になる (API の並び = master 初出順) */
  phases: string[];
  /** 人軸の行 (axis=member のとき描画) */
  memberRows: CcCommandMatrixMemberRow[];
  /** PJ 軸の行 (axis=project のとき描画) */
  projectRows: CcCommandMatrixProjectRow[];
  /** マスタの読み取り状態。stale なら警告を出す */
  master: CcCommandMatrixMasterMeta | undefined;
  /** fetch 中フラグ */
  isLoading: boolean;
  /** fetch 失敗フラグ */
  isError: boolean;
  /** 人軸で「データなし (totalEvents=0)」行を隠す */
  hideNoData: boolean;
  /** PJ 軸で完了済み PJ (ticketStatus=完了) も表示する。既定 false = 非表示 */
  showCompleted: boolean;
}

/** Backlog の完了ステータス値。これを持つ PJ を既定で隠す。@graph-connects none */
export const COMPLETED_TICKET_STATUS = '完了';

/** 人軸 1 行の状態。@graph-connects none */
type MemberStatus = 'active' | 'zero' | 'noData';

/** @graph-connects none */
function resolveMemberStatus(row: CcCommandMatrixMemberRow): MemberStatus {
  if (row.totalEvents === 0) return 'noData';
  return row.commandTotal > 0 ? 'active' : 'zero';
}

/** @graph-connects none */
const STATUS_BADGE_VARIANT: Record<MemberStatus, 'success' | 'warning' | 'error'> = {
  active: 'success',
  zero: 'warning',
  noData: 'error',
};

/** @graph-connects none */
const STATUS_ROW_CLASS: Record<MemberStatus, string> = {
  active: '',
  zero: 'bg-[var(--color-warning-muted)]',
  noData: 'opacity-60',
};

/** JST の日付 (YYYY-MM-DD) に整形する。@graph-connects none */
const JST_DATE_FORMAT = new Intl.DateTimeFormat('sv-SE', {
  timeZone: 'Asia/Tokyo',
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
});

/** @graph-connects none */
function formatJstDate(iso: string | null): string {
  if (!iso) return '–';
  const date = new Date(iso);
  return Number.isNaN(date.getTime()) ? '–' : JST_DATE_FORMAT.format(date);
}

/** @graph-connects none */
function formatNumber(value: number): string {
  return value.toLocaleString();
}

/** 行内訳モーダルを開くボタンセル。@graph-connects none */
function DetailButtonCell({ label, onOpen }: { label: string; onOpen: () => void }) {
  const { t } = useTranslation();
  return (
    <td className="py-2 pl-3 whitespace-nowrap">
      <button
        type="button"
        onClick={onOpen}
        aria-label={t('ccCommandMatrix.detail.openAria', { target: label })}
        className="text-xs underline text-[var(--color-accent-primary)] hover:text-[var(--color-accent-hover)]"
      >
        {t('ccCommandMatrix.detail.open')}
      </button>
    </td>
  );
}

/** @graph-connects none */
function SummaryCards({ items }: { items: { label: string; value: string }[] }) {
  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
      {items.map((item) => (
        <Card key={item.label}>
          <CardContent className="flex flex-col gap-1 px-4 py-3">
            <span className="text-xs text-[var(--color-text-muted)]">{item.label}</span>
            <span className="text-lg font-semibold text-[var(--color-text-primary)]">
              {item.value}
            </span>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

/**
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business cc-command-matrix dashboard panel (presentation のみ)
 * @graph-connects Badge [calls] 行の状態 (利用あり / 規定コマンド 0 / データなし / 母集団外 / Backlog ステータス) の表示
 */
export function CcCommandMatrixPanel({
  axis,
  phases,
  memberRows,
  projectRows,
  master,
  isLoading,
  isError,
  hideNoData,
  showCompleted,
}: CcCommandMatrixPanelProps) {
  const { t } = useTranslation();
  const [selection, setSelection] = useState<CcCommandMatrixDetailSelection | null>(null);

  if (isLoading) {
    return (
      <div className="h-48 flex items-center justify-center">
        <div className="loading-spinner" />
      </div>
    );
  }

  if (isError) {
    return (
      <Card>
        <CardContent className="py-6 text-center text-sm text-[var(--color-error)]">
          {t('ccCommandMatrix.loadError')}
        </CardContent>
      </Card>
    );
  }

  const visibleMemberRows = hideNoData
    ? memberRows.filter((row) => row.totalEvents > 0)
    : memberRows;
  const rosterRows = memberRows.filter((row) => row.inRoster);
  const memberSummary = [
    { label: t('ccCommandMatrix.summary.rosterCount'), value: formatNumber(rosterRows.length) },
    {
      label: t('ccCommandMatrix.summary.activeCount'),
      value: formatNumber(rosterRows.filter((row) => resolveMemberStatus(row) === 'active').length),
    },
    {
      label: t('ccCommandMatrix.summary.zeroCount'),
      value: formatNumber(rosterRows.filter((row) => resolveMemberStatus(row) === 'zero').length),
    },
    {
      label: t('ccCommandMatrix.summary.noDataCount'),
      value: formatNumber(rosterRows.filter((row) => resolveMemberStatus(row) === 'noData').length),
    },
  ];

  const visibleProjectRows = showCompleted
    ? projectRows
    : projectRows.filter((row) => row.ticketStatus !== COMPLETED_TICKET_STATUS);
  const completedHiddenCount = projectRows.length - visibleProjectRows.length;
  const linkedProjects = visibleProjectRows.filter((row) => row.ticketKey !== null);
  const totalProjectEvents = visibleProjectRows.reduce((sum, row) => sum + row.totalEvents, 0);
  const unlinkedEvents = visibleProjectRows
    .filter((row) => row.ticketKey === null)
    .reduce((sum, row) => sum + row.totalEvents, 0);
  const projectSummary = [
    {
      label: t('ccCommandMatrix.summary.projectCount'),
      value: formatNumber(linkedProjects.length),
    },
    {
      label: t('ccCommandMatrix.summary.unlinkedShare'),
      value:
        totalProjectEvents > 0
          ? `${String(Math.round((unlinkedEvents / totalProjectEvents) * 100))}%`
          : '–',
    },
  ];

  const columnCount = phases.length + 7;

  return (
    <div className="space-y-6">
      {master ? (
        <div className="space-y-2">
          {master.stale ? (
            <div
              role="alert"
              className="rounded-md border border-[var(--color-warning)] bg-[var(--color-warning-muted)] px-4 py-2 text-sm text-[var(--color-warning)]"
            >
              {t('ccCommandMatrix.masterStale', { warning: master.warning ?? '' })}
            </div>
          ) : null}
          <p className="text-xs text-[var(--color-text-muted)]">
            {t('ccCommandMatrix.masterMeta', {
              count: master.entryCount,
              fetchedAt: formatJstDate(master.fetchedAt),
            })}
            <a
              href={master.spreadsheetUrl}
              target="_blank"
              rel="noreferrer"
              className="ml-2 underline text-[var(--color-accent-primary)]"
            >
              {t('ccCommandMatrix.masterLink')}
            </a>
          </p>
        </div>
      ) : null}
      <SummaryCards items={axis === 'member' ? memberSummary : projectSummary} />
      {axis === 'project' && completedHiddenCount > 0 ? (
        <p className="text-xs text-[var(--color-text-muted)]">
          {t('ccCommandMatrix.completedHidden', { count: completedHiddenCount })}
        </p>
      ) : null}
      <Card>
        <CardHeader>
          <h2 className="text-sm font-semibold text-[var(--color-text-primary)]">
            {t(`ccCommandMatrix.axis.${axis}`)}
          </h2>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-[var(--color-text-muted)] border-b border-[var(--color-border)]">
                  {axis === 'member' ? (
                    <>
                      <th className="py-2 pr-4">{t('ccCommandMatrix.columns.member')}</th>
                      <th className="py-2 pr-4">{t('ccCommandMatrix.columns.team')}</th>
                    </>
                  ) : (
                    <>
                      <th className="py-2 pr-4">{t('ccCommandMatrix.columns.project')}</th>
                      <th className="py-2 pr-4 whitespace-nowrap">
                        {t('ccCommandMatrix.columns.ticketStatus')}
                      </th>
                      <th className="py-2 pr-4 whitespace-nowrap">
                        {t('ccCommandMatrix.columns.assignee')}
                      </th>
                    </>
                  )}
                  {phases.map((phase) => (
                    <th key={phase} className="py-2 px-3 text-right whitespace-nowrap">
                      {phase}
                    </th>
                  ))}
                  <th className="py-2 px-3 text-right whitespace-nowrap">
                    {t('ccCommandMatrix.columns.commandTotal')}
                  </th>
                  <th className="py-2 px-3 text-right whitespace-nowrap">
                    {t('ccCommandMatrix.columns.totalEvents')}
                  </th>
                  {axis === 'member' ? (
                    <>
                      <th className="py-2 px-3 text-right whitespace-nowrap">
                        {t('ccCommandMatrix.columns.lastEventAt')}
                      </th>
                      <th className="py-2 pl-3 whitespace-nowrap">
                        {t('ccCommandMatrix.columns.status')}
                      </th>
                    </>
                  ) : (
                    <th className="py-2 px-3 text-right whitespace-nowrap">
                      {t('ccCommandMatrix.columns.uniqueUsers')}
                    </th>
                  )}
                  <th className="py-2 pl-3 whitespace-nowrap">
                    {t('ccCommandMatrix.columns.detail')}
                  </th>
                </tr>
              </thead>
              <tbody>
                {axis === 'member' ? (
                  visibleMemberRows.length === 0 ? (
                    <tr>
                      <td
                        colSpan={columnCount}
                        className="py-4 text-center text-[var(--color-text-muted)]"
                      >
                        {t('ccCommandMatrix.empty')}
                      </td>
                    </tr>
                  ) : (
                    visibleMemberRows.map((row) => {
                      const status = resolveMemberStatus(row);
                      return (
                        <tr
                          key={row.email}
                          data-status={status}
                          className={`border-b border-[var(--color-border)] ${STATUS_ROW_CLASS[status]}`}
                        >
                          <td className="py-2 pr-4 text-[var(--color-text-primary)] whitespace-nowrap">
                            {row.nickname ?? t('ccCommandMatrix.unassigned')}
                            <span className="text-xs text-[var(--color-text-muted)] ml-2">
                              ({row.email})
                            </span>
                          </td>
                          <td className="py-2 pr-4 text-[var(--color-text-secondary)] whitespace-nowrap">
                            {row.teamName ?? t('ccCommandMatrix.unassigned')}
                          </td>
                          <PhaseBreakdownCells
                            phases={phases}
                            commandCounts={row.commandCounts}
                            onOpenDetail={() => {
                              setSelection({ kind: 'member', row });
                            }}
                          />
                          <CommandTotalBreakdownCell
                            commandTotal={row.commandTotal}
                            commandCounts={row.commandCounts}
                            onOpenDetail={() => {
                              setSelection({ kind: 'member', row });
                            }}
                          />
                          <td className="py-2 px-3 text-right tabular-nums text-[var(--color-text-secondary)]">
                            {formatNumber(row.totalEvents)}
                          </td>
                          <td className="py-2 px-3 text-right tabular-nums text-[var(--color-text-secondary)] whitespace-nowrap">
                            {formatJstDate(row.lastEventAt)}
                          </td>
                          <td className="py-2 pl-3 whitespace-nowrap">
                            <span className="inline-flex flex-wrap gap-1">
                              <Badge variant={STATUS_BADGE_VARIANT[status]} solid>
                                {t(`ccCommandMatrix.status.${status}`)}
                              </Badge>
                              {row.inRoster ? null : (
                                <Badge variant="default">
                                  {t('ccCommandMatrix.status.outsideRoster')}
                                </Badge>
                              )}
                            </span>
                          </td>
                          <DetailButtonCell
                            label={row.nickname ?? row.email}
                            onOpen={() => {
                              setSelection({ kind: 'member', row });
                            }}
                          />
                        </tr>
                      );
                    })
                  )
                ) : visibleProjectRows.length === 0 ? (
                  <tr>
                    <td
                      colSpan={columnCount}
                      className="py-4 text-center text-[var(--color-text-muted)]"
                    >
                      {t('ccCommandMatrix.empty')}
                    </td>
                  </tr>
                ) : (
                  visibleProjectRows.map((row) => (
                    <tr
                      key={row.ticketKey ?? '__unlinked__'}
                      className={`border-b border-[var(--color-border)] ${
                        row.ticketKey === null ? 'bg-[var(--color-bg-elevated)]' : ''
                      }`}
                    >
                      <td className="py-2 pr-4 text-[var(--color-text-primary)]">
                        {row.ticketKey ? (
                          <a
                            href={backlogTicketUrl(row.ticketKey)}
                            target="_blank"
                            rel="noreferrer"
                            className="underline text-[var(--color-accent-primary)] hover:text-[var(--color-accent-hover)]"
                          >
                            {row.ticketKey}
                          </a>
                        ) : (
                          <span className="italic text-[var(--color-text-muted)]">
                            {t('ccCommandMatrix.unlinkedProject')}
                          </span>
                        )}
                        {row.ticketSummary ? (
                          <span className="text-xs text-[var(--color-text-muted)] ml-2">
                            {row.ticketSummary}
                          </span>
                        ) : null}
                      </td>
                      <td className="py-2 pr-4 whitespace-nowrap">
                        {row.ticketStatus ? (
                          <Badge variant="default">{row.ticketStatus}</Badge>
                        ) : (
                          <span className="text-[var(--color-text-muted)]">–</span>
                        )}
                      </td>
                      <td className="py-2 pr-4 text-[var(--color-text-secondary)] whitespace-nowrap">
                        {row.mainWorker ?? row.assignee ?? '–'}
                      </td>
                      <PhaseBreakdownCells
                        phases={phases}
                        commandCounts={row.commandCounts}
                        onOpenDetail={() => {
                          setSelection({ kind: 'project', row });
                        }}
                      />
                      <CommandTotalBreakdownCell
                        commandTotal={row.commandTotal}
                        commandCounts={row.commandCounts}
                        onOpenDetail={() => {
                          setSelection({ kind: 'project', row });
                        }}
                      />
                      <td className="py-2 px-3 text-right tabular-nums text-[var(--color-text-secondary)]">
                        {formatNumber(row.totalEvents)}
                      </td>
                      <td className="py-2 px-3 text-right tabular-nums text-[var(--color-text-secondary)]">
                        {formatNumber(row.uniqueUsers)}
                      </td>
                      <DetailButtonCell
                        label={row.ticketKey ?? t('ccCommandMatrix.unlinkedProject')}
                        onOpen={() => {
                          setSelection({ kind: 'project', row });
                        }}
                      />
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
      <CcCommandMatrixDetailModal
        phases={phases}
        selection={selection}
        onClose={() => {
          setSelection(null);
        }}
      />
    </div>
  );
}
